import Icons, { SvgFile } from "../svg/Icons.ts";
import * as Html from "../util/Html.ts";
import * as FwDatas from "../FwDatas.ts";
import { emojiIntoField, emojiPicker } from "./action-menu-content.ts";

export type ActionMenu = {
  menu ?: (ActionMenuButton|{html:string})[],
  onclick ? : Function,
  //arbitrary markup rendered under the entries, with its own event handling: date picker, emoji
  //picker, quantity editor... see action-menu-content.ts
  content?: ActionMenuContent,
  init?:(this:HTMLElement)=>any
  onCancel?:()=>any
  //positioning, computed by the widget itself before rendering
  css?:{[key:string]:string}
}
export type ActionMenuContent = {
  html:string,
  /**
   * @param root the menu node, the content is its last child
   * @param close dismisses the menu without going through onCancel
   */
  init?:(root:HTMLElement,close:()=>void)=>void
}
export type ActionMenuButton = {
  separator?:boolean,
  permission ?:string,
  svg ?:SvgFile,
  svgend ?:SvgFile,
  img?:string,
  iconclass?:string,
  emoji?:string,
  onclick?:string,
  clickFunctionNoPermission?:()=>void,
  clickFunction?:()=>void
  selected?:boolean,
  title?:string
  i18nkey?:string,
  color?:string,
  opacity?:number
  value?:string,
  beta?:boolean,
  //with data-autofill, clicking this entry copies it into the button
  fillMenu?:boolean,
  submenu?:ActionMenuButton[],
  //renders the submenu already expanded
  open?:boolean
}

const MENU = new Map<string,(this:HTMLElement)=>ActionMenu|Promise<ActionMenu>>();

const EMOJI_SETS = {
  emojifood:"🍽️ 🥣 ☕ 🍫 🍿 🍪 🍭 🍬 🍛 🍲 🥘 🫕 🌯 🥗 🍔 🌭 🍗 🥐 🍞 🥦 🥑 🍒 🍓 🍎 🍋 🥙 🍕 🍱 🍜 🥞 🍩 🍰 🍊 🥨 🥪 🦴",
  emojibudget:"🤷 💄 🍽️ 🚗 👕 🩺 📚 ⚡️ 🕹 🍿 🎁 🛒 💊 🏠 🛡 💵 🐶 🏦 💰 ⚽️ 📅 🚉 ✈️ 🏝 💸 🍔 🏧 🧸 ☎️ 📈 👔 💳 👥",
  emojitext:"😀 😂 😃 😄 😅 😆 😉 😊 😋 😌 😍 😏 😒 😓 😔 😖 😘 😚 😜 😝 😞 😠 😡 😢 😣 😤 😥 😨 😩 😪 😫 😭 😰 😱 😲 😳 😵 😷 😸 😹 😺 😻 😼 😽 😾 😿 🙀 🙅 🙆 🙇 🙈 🙉 🙊 🙋 🙌 🙍 🙎 🙏 😀 😇 😈 😎 😐 😑 😕 😗 😙 😛 😟 😦 😧 😬 😮 😯 😴 😶 ❤️ ✌ 🚼 ☕",
  emojitimetable:"⚽️ 🏀 🏈 ⚾️ 🥎 🎾 🏐 🏉 🏓 🎨 🎭 🎻 🍽️ 🎼 🎹 🏆 🏫 🎓 📖 🏥 🏨 🏠 🏖 🧼 🛍 🛒 🎉 🗓 📅 💼 🔁 ❤️ 🎟 ✈️ ⛵️ 💊",
  emojilistcategory:"🥬 🧀 🥑 🥥 🎒 👕 👗 👟 👠 🔨 💊 👓 🌳 🌿 🌸 🍭 🍵 🍷 👜 💼 📚 💍 💡 🧸 🎁 🧺 ✂️ 🖌 🪑 🏺 🎮 🖥 ⚽️ 🎨 🎧 🎭 🐶 🐱 🐹 🐰 🐠 🐓",
  emojifiler:"📁 📂 🗂 🏝 🏖 🎁 🏠 📊 🏥 🥘 🛠 🎓 🏫 📚 💰 📄 💼 🔐 🤫 🎥 📷 🌈 🖼 🎁 🛍 🛒 📋 🔍 ❤️ 🎵 🥳 🧠 🐶 🐱 ☀️ ⭐️"
}
Object.entries(EMOJI_SETS).forEach(([key,emojis])=>{
  MENU.set(key,function(){
    return {content:emojiPicker(emojis.split(' '),emojiIntoField(this))}
  })
})


Html.registerDom({
  togglemenu(){
    let sub = this.nextElementSibling as HTMLElement;
    const open = !sub?.classList.contains('open');

    while (sub && sub.classList.contains('sub')) {
      sub.classList.toggle('open',open);
      sub = sub.nextElementSibling as HTMLElement | null;
    }
    const children = this.children;
    if (children.length > 0) {
      children[children.length - 1].classList.toggle('rotate180');
    }
    return false;
  }
})
export function register(obj : {[key:string]:(this:HTMLElement)=>ActionMenu|Promise<ActionMenu>}){
  Object.entries(obj).forEach(([key,func])=>{
    if(MENU.has(key)){
      throw new Error("cannot define the same action twice : "+key);
    }else{
      MENU.set(key,func);
    }
  });
}
export async function getData(o :HTMLElement,key = o.getAttribute('data-key')){
  const generator = MENU.get(key);
  if(!generator) throw new Error('missing action menu key '+key)

  const generated = await generator.call(o);

  generated.menu = generated.menu?.map(button=>{
    if('html' in button) return button;
    if(!button.permission) return button;

    switch (FwDatas.getPermission(button.permission)){
      case 'KO_PREMIUM' :
        //the entry stays visible but its own action is replaced by the paywall
        return {
          ...button,
          svgend:Icons.ic_premium_feature_indicator,
          onclick:button.clickFunctionNoPermission?undefined:"takepremium",
          clickFunction:button.clickFunctionNoPermission
        }
      case 'KO_HIDE' : return undefined;
      default : return button;
    }
  }).filter(obj=>obj!==undefined)

  return generated;
}